import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { CircleNotch, Plus } from '@phosphor-icons/react';
import type { MappedApiError } from '@mybusinessmate/api-client';
import type { StaffRole, StaffRow } from '@mybusinessmate/schema';
import { AddStaffDialog } from '../../../../../components/staff/add-staff-dialog';
import { EditStaffDialog } from '../../../../../components/staff/edit-staff-dialog';
import { StaffRow as Row, StaffRowsHeader, StaffRowSkeleton } from '../../../../../components/staff/staff-row';
import { SuspendStaffDialog } from '../../../../../components/staff/suspend-staff-dialog';
import { DirectoryHeader } from '../../../../../components/directory/directory-header';
import { Icon } from '../../../../../components/Icon';
import { EmptyState } from '../../../../../components/ui/empty-state';
import { InfiniteList } from '../../../../../components/ui/infinite-list';
import { useToast } from '../../../../../components/ui/toast';
import { FilterSelectPill, PillButton, ToolbarSearch } from '../../../../../components/ui/toolbar-controls';
import { UnderlineTabs, type UnderlineTab } from '../../../../../components/ui/underline-tabs';
import { directoryKey, staffListQuery, useReinstateStaff } from '../../../../../lib/directory-api';
import { requireAdmin } from '../../../../../lib/guards';
import { useSession } from '../../../../../lib/session';
import { useDebouncedValue } from '../../../../../lib/use-debounced-value';
import { NoResults } from '../../../../../components/ui/state-views';
import { toasts } from '../../../../../lib/toast-presets';

const TABS = ['all', 'admin', 'account_manager', 'lead_specialist', 'specialist', 'suspended'] as const;
type Tab = (typeof TABS)[number];

export const Route = createFileRoute('/subdomain/admin/_staff/staff/')({
  beforeLoad: ({ context }) => requireAdmin(context.queryClient),
  validateSearch: (search: Record<string, unknown>): { tab?: Tab; q?: string } => ({
    ...((TABS as readonly unknown[]).includes(search.tab) && search.tab !== 'all' ? { tab: search.tab as Tab } : {}),
    ...(typeof search.q === 'string' && search.q ? { q: search.q } : {}),
  }),
  // The tables are 1200 wide in the design (same canvas as the settings pages).
  staticData: { wide: true },
  component: StaffPage,
});

/** A tab is a role, or the Suspended view; "All" has neither. */
const filtersFor = (tab: Tab): { role?: StaffRole; status?: 'suspended' } =>
  tab === 'all' ? {} : tab === 'suspended' ? { status: 'suspended' } : { role: tab };

const ROLE_OPTIONS: { value: StaffRole; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'account_manager', label: 'Account manager' },
  { value: 'lead_specialist', label: 'Lead specialist' },
  { value: 'specialist', label: 'Specialist' },
];

/**
 * The team roster (Figma A04.1): who they are, their skills and workload, when they were last on, and admin actions.
 * Controls the design shows that the product cannot back yet (the Invited view, department / skills / status / load /
 * last-active filters, load sorting) are drawn as designed and answer "coming soon".
 */
function StaffPage() {
  const { tab = 'all', q } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data: session } = useSession();
  const [text, setText] = useState(q ?? '');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<StaffRow | null>(null);
  const [suspending, setSuspending] = useState<StaffRow | null>(null);
  const [reinstating, setReinstating] = useState<StaffRow | null>(null);
  const search = useDebouncedValue(text.trim(), 250) || undefined;
  if (search !== q) void navigate({ search: (prev) => ({ ...prev, q: search }), replace: true });

  const list = useInfiniteQuery(staffListQuery({ ...filtersFor(tab), q: search }));
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const counts = list.data?.pages[0]?.counts;
  const filtered = !!search || tab !== 'all';
  const soon = () => toast(toasts.comingSoon('This filter isn’t available yet.'));
  const setTab = (next: Tab) =>
    void navigate({ search: (prev) => ({ ...prev, tab: next === 'all' ? undefined : next }) });

  const reinstate = useReinstateStaff(reinstating?.id ?? '');
  const lift = async (person: StaffRow) => {
    setReinstating(person);
    try {
      await reinstate.mutateAsync();
      toast({ title: `${person.name} has access again`, description: 'Their old assignments weren’t restored.' });
    } catch (err) {
      toast({
        title: 'Couldn’t reinstate',
        description: (err as Partial<MappedApiError>).message ?? 'Please try again.',
        tone: 'error',
      });
    } finally {
      setReinstating(null);
    }
  };

  const tabs: UnderlineTab<Tab | 'invited'>[] = [
    { value: 'all', label: 'All', count: counts?.all },
    { value: 'admin', label: 'Admins', count: counts?.admin },
    { value: 'account_manager', label: 'Account managers', count: counts?.account_manager },
    { value: 'lead_specialist', label: 'Lead specialists', count: counts?.lead_specialist },
    { value: 'specialist', label: 'Specialists', count: counts?.specialist },
    { value: 'invited', label: 'Invited', unavailable: true },
    { value: 'suspended', label: 'Suspended', count: counts?.suspended },
  ];

  return (
    <>
      <DirectoryHeader
        title="Staff"
        description={
          counts ? `${counts.all} ${counts.all === 1 ? 'person' : 'people'} · ${counts.suspended} suspended` : undefined
        }
        actions={
          <PillButton label="Add staff" caret={false} icon={Plus} dark onClick={() => setAdding(true)}>
            Add staff
          </PillButton>
        }
      />

      <UnderlineTabs
        aria-label="Views"
        tabs={tabs}
        value={tab}
        onChange={(next) => setTab(next as Tab)}
        onUnavailable={soon}
      />

      <div className="flex flex-wrap items-center gap-(--mbm-space-2)">
        <ToolbarSearch
          aria-label="Search staff"
          placeholder="Search name, email, phone, skill…"
          value={text}
          onChange={setText}
          className="w-full sm:w-(--mbm-layout-toolbar-search-width)"
        />
        <FilterSelectPill
          label="Role"
          value={ROLE_OPTIONS.some((r) => r.value === tab) ? tab : ''}
          onChange={(value) => setTab((value || 'all') as Tab)}
          options={ROLE_OPTIONS}
        />
        <PillButton label="Department" onClick={soon} />
        <PillButton label="Skills" onClick={soon} />
        <PillButton label="Status" onClick={soon} />
        <PillButton label="Load" onClick={soon} />
        <PillButton label="Last active" onClick={soon} />
        <PillButton label="More filters" caret={false} icon={Plus} onClick={soon}>
          More
        </PillButton>
        <span className="ml-auto">
          {/* The roster is alphabetical. The design's "Load: highest first" order is not claimed here. */}
          <PillButton label="Sort" onClick={soon}>
            Name: A to Z
          </PillButton>
        </span>
      </div>

      <div className="overflow-hidden rounded-(--mbm-component-radius-card) bg-surface-card shadow-[var(--mbm-shadow-table-card),var(--mbm-shadow-sm)]">
        <StaffRowsHeader />
        <InfiniteList
          items={items}
          getKey={(p) => p.id}
          renderItem={(p) => (
            <Row
              person={p}
              isSelf={p.id === session?.user.id}
              actions={{
                onView: () => void navigate({ to: '/subdomain/admin/staff/$staffId', params: { staffId: p.id } }),
                onEdit: () => setEditing(p),
                onSuspend: () => setSuspending(p),
                onReinstate: () => void lift(p),
              }}
            />
          )}
          hasNextPage={list.hasNextPage}
          isFetchingNextPage={list.isFetchingNextPage}
          fetchNextPage={() => void list.fetchNextPage()}
          isLoading={list.isLoading}
          isError={list.isError}
          onRetry={() => void list.refetch()}
          emptyState={
            filtered ? (
              <NoResults
                query={search}
                description="Try another tab or search."
                onClear={() => {
                  setText('');
                  void navigate({ search: { tab: undefined, q: undefined } });
                }}
              />
            ) : (
              <EmptyState title="No staff yet" description="Add the first person with Add staff." />
            )
          }
          skeletonRow={<StaffRowSkeleton />}
          skeletonCount={2}
        />
        {list.hasNextPage || list.isFetchingNextPage ? (
          <p className="flex items-center justify-center gap-(--mbm-space-2) py-(--mbm-space-3-5) text-body-small text-text-tertiary">
            <Icon icon={CircleNotch} size="sm" />
            Loading more{counts && !filtered ? ` · showing ${items.length} of ${counts.all}` : ''}
          </p>
        ) : null}
      </div>

      <AddStaffDialog
        open={adding}
        onClose={() => setAdding(false)}
        onInvited={() => void queryClient.invalidateQueries({ queryKey: directoryKey })}
      />
      <EditStaffDialog person={editing} onClose={() => setEditing(null)} />
      <SuspendStaffDialog person={suspending} onClose={() => setSuspending(null)} />
    </>
  );
}
